import { useEffect, useRef, useState, type MutableRefObject } from "react";

/** Generic media-query hook. Mirrors the Writer's Room's
 * `useIsMobileWritingRoomLayout` pattern (WritingRoomLayer.tsx): lazy init
 * from `matchMedia` so the first render already has the right value, plus a
 * ref written in the SAME TICK as the change handler (not from a follow-up
 * effect) for callers that need the current value outside React's render
 * cycle — e.g. a keydown handler that fires between a resize and React's
 * next commit. */
export function useMediaQuery(query: string): { matches: boolean; matchesRef: MutableRefObject<boolean> } {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false,
  );
  const matchesRef = useRef(matches);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    matchesRef.current = mql.matches;
    setMatches(mql.matches);
    const handler = (event: MediaQueryListEvent) => {
      matchesRef.current = event.matches;
      setMatches(event.matches);
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return { matches, matchesRef };
}
